// App store: in-memory state + actions. Every action updates state optimistically, then
// persists through the DataSource; a failed save shows a toast and reloads from the source.

import { useMemo, useSyncExternalStore } from 'react';
import type {
  InboundEmail,
  ListItem,
  Order,
  PersonId,
  Segment,
  Staple,
  StoreId,
  StoreProduct,
  StoreSettings,
} from '@shared/types.ts';
import { newId, normalizeName, roundMoney, sentenceCase } from '@shared/text.ts';
import type { StorePlan } from '@shared/routing.ts';
import { mergeIntoTrips } from '@shared/ingest/merge.ts';
import { withStayZone } from '@shared/stayZones.ts';
import { validateSegments } from '@shared/ingest/validate.ts';
import type { ExtractedSegment } from '@shared/ingest/extracted.ts';
import { type FeatureKey, isOn, OWNER, type Stage, withFinished, withStage } from '@shared/features.ts';
import { answerNote, type Note, type NoteTarget, reopenNote } from '@shared/notes.ts';
import type { SupabaseSource } from './supabaseSource.ts';
import { safeLocalStorage } from './localSource.ts';
import { clearSnapshot, loadSnapshot, saveSnapshot } from './snapshot.ts';
import { clearCopies } from '../features/language/data/offline.ts';
import type { AppSettings, AppState } from './state.ts';
import type { Collection, DataSource, PersonPatch, Row } from './source.ts';

export interface Toast {
  id: string;
  text: string;
  actionLabel?: string;
  action?: () => void;
}

export interface UiState {
  ready: boolean;
  error?: string;
  /** Can't reach the server: showing the copy saved on this phone, and edits are refused. */
  offline?: boolean;
  /** When the data on screen was last confirmed by the server (ISO). */
  savedAt?: string;
  toasts: Toast[];
  /** Settings → Developer → "See what Natalie sees": switches act as if she were using this phone. */
  viewAs?: PersonId;
}

export const OFFLINE_EDIT = "You're offline. Changes are off until you reconnect.";
export const OFFLINE_FIRST_LAUNCH = "You're offline. Connect once to load your trips and list; after that they open without signal.";

const VIEW_AS_KEY = 'natangelo:view-as';

const deviceOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

type Listener = () => void;

export class AppStore {
  state!: AppState;
  ui: UiState = { ready: false, toasts: [] };
  private listeners = new Set<Listener>();
  private unsubscribe?: () => void;
  private reloadTimer?: ReturnType<typeof setTimeout>;
  private pendingReload = new Set<Collection>();

  /** Shared data keeps a copy on this phone for offline use; local mode already lives here. */
  private readonly keepsCopy: boolean;
  private unhookNetwork?: () => void;

  constructor(
    readonly source: DataSource,
    private readonly storage: Storage | undefined = safeLocalStorage(),
  ) {
    this.keepsCopy = source.kind === 'supabase';
    if (this.keepsCopy) this.hookNetwork();
    try {
      const viewAs = this.storage?.getItem(VIEW_AS_KEY);
      if (viewAs) this.ui = { ...this.ui, viewAs: viewAs as PersonId };
    } catch {
      // storage unavailable: start as yourself
    }
  }

  async init(): Promise<void> {
    const copy = this.keepsCopy ? loadSnapshot(this.storage) : undefined;
    // Open instantly with the saved copy; the server's answer replaces it below.
    if (!this.ui.ready && copy) {
      // Copies saved before switches (or notes) existed have none: every switch then counts as Just me.
      this.state = { ...copy.state, features: copy.state.features ?? [], notes: copy.state.notes ?? [] };
      this.ui = { ...this.ui, ready: true, savedAt: copy.savedAt };
      this.emit();
    }
    try {
      this.state = await this.source.load();
      this.ui = { ...this.ui, ready: true, error: undefined, offline: false };
      this.saveCopy();
      this.unsubscribe?.();
      this.unsubscribe = this.source.subscribe((c) => this.scheduleReload(c));
    } catch (e) {
      if (copy) {
        // Show what the server last confirmed, which also drops an edit that didn't save.
        this.state = { ...copy.state, features: copy.state.features ?? [], notes: copy.state.notes ?? [] };
        this.ui = { ...this.ui, ready: true, error: undefined, offline: true, savedAt: copy.savedAt };
      } else {
        this.ui = { ...this.ui, ready: false, error: deviceOffline() ? OFFLINE_FIRST_LAUNCH : (e as Error).message };
      }
    }
    this.emit();
  }

  dispose(): void {
    this.unsubscribe?.();
    this.unhookNetwork?.();
  }

  /** Sign out of the shared data and forget the copy kept on this phone. */
  async signOut(): Promise<void> {
    clearSnapshot(this.storage);
    clearCopies(this.storage);
    await (this.source as SupabaseSource).signOut();
  }

  /** Going offline refuses edits right away; coming back (or reopening the app) reloads. */
  private hookNetwork(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const offline = () => {
      if (!this.ui.ready || this.ui.offline) return;
      this.ui = { ...this.ui, offline: true };
      this.emit();
    };
    const retry = () => {
      if (document.visibilityState === 'hidden') return;
      if (this.ui.offline || !this.ui.ready) void this.init();
    };
    window.addEventListener('offline', offline);
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', retry);
    this.unhookNetwork = () => {
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', retry);
    };
  }

  private saveCopy(): void {
    if (!this.keepsCopy) return;
    const savedAt = new Date().toISOString();
    saveSnapshot(this.storage, this.state, savedAt);
    this.ui = { ...this.ui, savedAt };
  }

  // ------------------------------------------------------------ plumbing

  subscribe = (l: Listener): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  private emit(): void {
    for (const l of this.listeners) l();
  }

  private set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    this.emit();
  }

  private scheduleReload(c: Collection | 'all'): void {
    if (c === 'all') {
      void this.init();
      return;
    }
    this.pendingReload.add(c);
    clearTimeout(this.reloadTimer);
    this.reloadTimer = setTimeout(async () => {
      const cs = [...this.pendingReload];
      this.pendingReload.clear();
      try {
        const results = await Promise.all(cs.map((x) => this.source.reload(x)));
        const patch: Partial<AppState> = {};
        cs.forEach((x, i) => ((patch as Record<string, unknown>)[x] = results[i]));
        this.set(patch);
        if (this.pending === 0) this.saveCopy();
      } catch {
        // next visibility change will retry
      }
    }, 250);
  }

  // Writes run one at a time, in order, so a staple is saved before the items that reference it.
  private queue: Promise<unknown> = Promise.resolve();
  private pending = 0;

  private persist(work: () => Promise<unknown>): void {
    this.pending += 1;
    this.queue = this.queue.then(work).then(
      () => {
        this.pending -= 1;
        // Everything on screen is now saved: that's the copy to keep.
        if (this.pending === 0) this.saveCopy();
      },
      (e: Error) => {
        this.pending -= 1;
        this.toast(deviceOffline() ? "You're offline, so that change wasn't saved." : `Couldn't save: ${e.message}`);
        void this.init();
      },
    );
  }

  /** Offline, edits are refused with one message per action (not one per row it writes). */
  private refusing = false;
  private canWrite(): boolean {
    if (!this.ui.offline) return true;
    if (!this.refusing) {
      this.refusing = true;
      this.addToast(OFFLINE_EDIT);
      // The action's own toasts ("Deleted X · Undo") are skipped until it returns.
      queueMicrotask(() => (this.refusing = false));
    }
    return false;
  }

  private put<C extends Collection>(collection: C, rows: Row<C>[]): void {
    if (rows.length === 0 || !this.canWrite()) return;
    const current = this.state[collection] as Row<C>[];
    const ids = new Set(rows.map((r) => (r as { id: string }).id));
    this.set({ [collection]: [...current.filter((r) => !ids.has((r as { id: string }).id)), ...rows] } as Partial<AppState>);
    this.persist(() => this.source.upsert(collection, rows));
  }

  private drop(collection: Collection, ids: string[]): void {
    if (ids.length === 0 || !this.canWrite()) return;
    const set = new Set(ids);
    this.set({ [collection]: (this.state[collection] as Array<{ id: string }>).filter((r) => !set.has(r.id)) } as Partial<AppState>);
    this.persist(() => this.source.remove(collection, ids));
  }

  toast(text: string, actionLabel?: string, action?: () => void): void {
    if (this.refusing) return;
    this.addToast(text, actionLabel, action);
  }

  private addToast(text: string, actionLabel?: string, action?: () => void): void {
    // The same message twice in a row (e.g. several failed saves) shows once.
    if (this.ui.toasts.some((t) => t.text === text && !t.action)) return;
    const t: Toast = { id: newId('t_'), text, actionLabel, action };
    this.ui = { ...this.ui, toasts: [...this.ui.toasts, t] };
    this.emit();
    setTimeout(() => this.dismissToast(t.id), action ? 6000 : 3500);
  }

  dismissToast(id: string): void {
    this.ui = { ...this.ui, toasts: this.ui.toasts.filter((t) => t.id !== id) };
    this.emit();
  }

  private get me(): PersonId {
    return this.state.me ?? 'michael';
  }

  private now(): string {
    return new Date().toISOString();
  }

  // ------------------------------------------------------------ identity

  setMe(id: PersonId): void {
    this.set({ me: id });
    if (this.source.saveMe) this.persist(() => this.source.saveMe!(id));
  }

  // ------------------------------------------------------------ feature switches

  /** The signed-in person (local mode without a choice yet counts as Michael). */
  private get signedIn(): PersonId | undefined {
    return this.state?.me ?? (this.source.kind === 'local' ? OWNER : undefined);
  }

  /** Michael, who sees Settings → Developer even while seeing what Natalie sees. */
  get isOwner(): boolean {
    return this.signedIn === OWNER;
  }

  /** Who the switches are checked for: you, or Natalie while "See what Natalie sees" is on. */
  get viewer(): PersonId | undefined {
    return this.ui.viewAs ?? this.signedIn;
  }

  isOn(key: FeatureKey): boolean {
    return isOn(this.state?.features, key, this.viewer);
  }

  /** Only Michael sees the controls; the database refuses anyone else's change too. */
  setFeatureStage(key: FeatureKey, stage: Stage): void {
    const row = this.state.features.find((f) => f.id === key);
    if (row?.stage === stage) return;
    this.put('features', [withStage(row, key, stage, this.now())]);
  }

  /** Finish rollout (or Undo) on a switch at Everyone: the feature stays on, the switch waits for cleanup. */
  setFeatureFinished(key: FeatureKey, finished: boolean): void {
    const row = this.state.features.find((f) => f.id === key);
    const next = withFinished(row, finished, this.now());
    if (!next || !row || !!next.finishedAt === !!row.finishedAt) return;
    this.put('features', [next]);
  }

  /** This phone only; nothing changes for anyone else. */
  setViewAs(person: PersonId | undefined): void {
    try {
      if (person) this.storage?.setItem(VIEW_AS_KEY, person);
      else this.storage?.removeItem(VIEW_AS_KEY);
    } catch {
      // storage unavailable: lasts until the app closes
    }
    this.ui = { ...this.ui, viewAs: person };
    this.emit();
  }

  // ------------------------------------------------------------ notes

  /** Saved only once the server has it; false (with a toast) when it didn't, so the text can stay in the box. */
  async addNote(route: string, body: string, target?: NoteTarget): Promise<boolean> {
    const text = body.trim();
    if (!text || !this.canWrite()) return false;
    const now = this.now();
    const row: Note = { id: newId('note_'), createdAt: now, updatedAt: now, route, target, body: text, author: this.me, status: 'open' };
    const save = this.queue.catch(() => undefined).then(() => this.source.upsert('notes', [row]));
    this.queue = save.catch(() => undefined);
    try {
      await save;
    } catch (e) {
      this.toast(deviceOffline() ? "You're offline, so the note wasn't saved." : `Note NOT saved: ${(e as Error).message}`);
      return false;
    }
    this.set({ notes: [...this.state.notes, row] });
    if (this.pending === 0) this.saveCopy();
    this.toast('Note saved');
    return true;
  }

  private note(id: string): Note | undefined {
    return this.state.notes.find((n) => n.id === id);
  }

  editNote(id: string, body: string): void {
    const n = this.note(id);
    const text = body.trim();
    if (n && text && text !== n.body) this.put('notes', [{ ...n, body: text, updatedAt: this.now() }]);
  }

  answerNote(id: string, answer: string): void {
    const n = this.note(id);
    if (n && answer.trim()) this.put('notes', [answerNote(n, answer, this.now())]);
  }

  reopenNote(id: string, why: string): void {
    const n = this.note(id);
    if (n) this.put('notes', [reopenNote(n, why, this.now())]);
  }

  deleteNote(id: string): void {
    const n = this.note(id);
    if (!n) return;
    this.drop('notes', [id]);
    this.toast('Note deleted', 'Undo', () => this.put('notes', [n]));
  }

  // ------------------------------------------------------------ list

  stapleByName(name: string): Staple | undefined {
    const key = normalizeName(name);
    return this.state.staples.find((s) => normalizeName(s.name) === key);
  }

  /** Add an item, merging with an identical one already needed. */
  addItem(rawName: string, qty = 1, stapleId?: string): void {
    const name = sentenceCase(rawName.replace(/\s+/g, ' '));
    if (!name) return;
    const now = this.now();
    const staple = stapleId ? this.state.staples.find((s) => s.id === stapleId) : this.stapleByName(name);
    const key = normalizeName(name);
    const existing = this.state.list.find((i) => i.status === 'needed' && normalizeName(i.name) === key);
    if (existing) {
      this.put('list', [{ ...existing, qty: existing.qty + qty, updatedAt: now }]);
      this.toast(`${existing.name}: now ${existing.qty + qty}`);
    } else {
      this.put('list', [
        { id: newId('li_'), name: staple?.name ?? name, qty, stapleId: staple?.id, addedBy: this.me, addedAt: now, updatedAt: now, status: 'needed' },
      ]);
    }
    this.put('events', [{ id: newId('ev_'), name: staple?.name ?? name, stapleId: staple?.id, at: now, by: this.me }]);
  }

  addUsual(): void {
    const usual = this.state.staples.filter((s) => s.usual);
    if (usual.length === 0) {
      this.toast('No usual items yet. Mark staples as usual in Settings.');
      return;
    }
    const onList = new Set(this.state.list.filter((i) => i.status === 'needed').map((i) => normalizeName(i.name)));
    const toAdd = usual.filter((s) => !onList.has(normalizeName(s.name)));
    for (const s of toAdd) this.addItem(s.name, s.usualQty, s.id);
    this.toast(toAdd.length ? `Added ${toAdd.length} usual item${toAdd.length === 1 ? '' : 's'}` : 'The usual items are already on the list');
  }

  setQty(itemId: string, qty: number): void {
    const item = this.state.list.find((i) => i.id === itemId);
    if (!item) return;
    if (qty <= 0) {
      this.removeItem(itemId);
      return;
    }
    this.put('list', [{ ...item, qty, updatedAt: this.now() }]);
  }

  renameItem(itemId: string, name: string): void {
    const item = this.state.list.find((i) => i.id === itemId);
    const clean = sentenceCase(name);
    if (!item || !clean) return;
    const staple = this.stapleByName(clean);
    this.put('list', [{ ...item, name: clean, stapleId: staple?.id, updatedAt: this.now() }]);
  }

  /** Remove (e.g. checked off / not needed), with undo. */
  removeItem(itemId: string): void {
    const item = this.state.list.find((i) => i.id === itemId);
    if (!item) return;
    this.drop('list', [itemId]);
    this.toast(`Removed ${item.name}`, 'Undo', () => this.put('list', [{ ...item, updatedAt: this.now() }]));
  }

  setItemStore(itemIds: string[], storeId: StoreId | undefined): void {
    const now = this.now();
    const rows = this.state.list.filter((i) => itemIds.includes(i.id)).map((i) => ({ ...i, storeOverride: storeId, updatedAt: now }));
    this.put('list', rows);
  }

  // ------------------------------------------------------------ orders

  placeOrder(plan: StorePlan): void {
    const now = this.now();
    const order: Order = {
      id: newId('ord_'),
      storeId: plan.storeId,
      status: 'placed',
      placedAt: now,
      placedBy: this.me,
      estTotal: roundMoney(plan.subtotal + plan.fee),
      lines: plan.lines.map((l) => ({ listItemId: l.item.id, name: l.item.name, qty: l.item.qty, estPrice: l.unitPrice })),
    };
    this.put('orders', [order]);
    const ids = new Set(plan.lines.map((l) => l.item.id));
    this.put(
      'list',
      this.state.list.filter((i) => ids.has(i.id)).map((i) => ({ ...i, status: 'ordered' as const, orderId: order.id, storeOverride: undefined, updatedAt: now })),
    );
    this.toast(`${plan.storeName} order marked placed`, 'Undo', () => this.undoOrder(order.id));
  }

  undoOrder(orderId: string): void {
    const order = this.state.orders.find((o) => o.id === orderId);
    if (!order) return;
    const now = this.now();
    this.put(
      'list',
      this.state.list.filter((i) => i.orderId === orderId).map((i) => ({ ...i, status: 'needed' as const, orderId: undefined, updatedAt: now })),
    );
    this.drop('orders', [orderId]);
  }

  markDelivered(orderId: string): void {
    const order = this.state.orders.find((o) => o.id === orderId);
    if (!order) return;
    this.put('orders', [{ ...order, status: 'delivered' }]);
    this.drop('list', this.state.list.filter((i) => i.orderId === orderId).map((i) => i.id));
  }

  // ------------------------------------------------------------ staples & matches

  upsertStaple(staple: Staple): void {
    this.put('staples', [staple]);
  }

  deleteStaple(stapleId: string): void {
    this.drop('products', this.state.products.filter((p) => p.stapleId === stapleId).map((p) => p.id));
    this.drop('staples', [stapleId]);
  }

  upsertProduct(product: StoreProduct): void {
    this.put('products', [{ ...product, updatedAt: this.now() }]);
  }

  deleteProduct(productId: string): void {
    this.drop('products', [productId]);
  }

  /** The item's staple, created (and saved) if this item doesn't have one yet. */
  private ensureStaple(item: ListItem): Staple {
    let staple = item.stapleId ? this.state.staples.find((s) => s.id === item.stapleId) : this.stapleByName(item.name);
    if (!staple) {
      staple = { id: newId('st_'), name: item.name, usualQty: item.qty, usual: false };
      this.put('staples', [staple]);
    }
    return staple;
  }

  /** Perishable or pantry, remembered on the item's staple. */
  setItemPerishable(itemId: string, perishable: boolean): void {
    const item = this.state.list.find((i) => i.id === itemId);
    if (!item) return;
    this.put('staples', [{ ...this.ensureStaple(item), perishable }]);
  }

  /** "Ask once": match a list item to a product at a store, creating the staple if needed. */
  matchItem(itemId: string, match: Omit<StoreProduct, 'id' | 'stapleId' | 'updatedAt' | 'available'>): void {
    const item = this.state.list.find((i) => i.id === itemId);
    if (!item) return;
    const staple = this.ensureStaple(item);
    const existing = this.state.products.find((p) => p.stapleId === staple.id && p.storeId === match.storeId);
    this.put('products', [{ ...existing, ...match, id: existing?.id ?? newId('sp_'), stapleId: staple.id, available: true, updatedAt: this.now() }]);
    // Link every needed item with this name to the staple.
    const key = normalizeName(staple.name);
    this.put(
      'list',
      this.state.list.filter((i) => i.status === 'needed' && !i.stapleId && normalizeName(i.name) === key).map((i) => ({ ...i, stapleId: staple.id })),
    );
  }

  updateStore(store: StoreSettings): void {
    this.put('stores', [store]);
  }

  updateSettings(patch: Partial<AppSettings>): void {
    if (!this.canWrite()) return;
    const settings = { ...this.state.settings, ...patch };
    this.set({ settings });
    this.persist(() => this.source.saveSettings(settings));
  }

  /** Display name and booking names (Settings → People). */
  updatePerson(id: PersonId, patch: PersonPatch): void {
    if (!this.canWrite()) return;
    const clean: PersonPatch = { name: patch.name.trim(), bookingNames: patch.bookingNames.map((n) => n.trim()).filter(Boolean) };
    this.set({ people: this.state.people.map((p) => (p.id === id ? { ...p, ...clean } : p)) });
    this.persist(() => this.source.updatePerson(id, clean));
  }

  // ------------------------------------------------------------ trips

  /** "Who's going?" answered: applies to every leg booked under the same code. */
  setWho(segmentId: string, who: PersonId[]): void {
    const seg = this.state.segments.find((s) => s.id === segmentId);
    if (!seg) return;
    const code = seg.confirmation?.toUpperCase();
    const legs = code ? this.state.segments.filter((s) => s.confirmation?.toUpperCase() === code) : [seg];
    this.put(
      'segments',
      legs.map((s) => ({ ...s, who, updatedAt: this.now() })),
    );
  }

  /** Save a segment typed by hand (new or edited), merging into trips like an email would. */
  saveManualSegment(seg: ExtractedSegment, existingId?: string): { ok: boolean; problems: string[] } {
    if (this.ui.offline) return { ok: false, problems: [OFFLINE_EDIT] };
    const v = validateSegments([seg]);
    if (v.failed.length) return { ok: false, problems: v.failed[0].problems };
    const valid = v.ok[0];
    if (existingId) {
      const current = this.state.segments.find((s) => s.id === existingId);
      if (!current) return { ok: false, problems: ['That item no longer exists'] };
      const updated: Segment = withStayZone({
        ...current,
        ...valid,
        title: valid.title ?? current.title,
        travelers: valid.travelers ?? current.travelers,
        details: { ...current.details, ...valid.details },
        status: valid.cancelled ? 'cancelled' : 'active',
        updatedAt: this.now(),
      }, this.state.segments);
      // The price is for the whole booking: keep every leg with the same code in step.
      const siblings =
        updated.price !== current.price && updated.confirmation
          ? this.state.segments
              .filter((s) => s.id !== existingId && s.confirmation?.toUpperCase() === updated.confirmation!.toUpperCase())
              .map((s) => ({ ...s, price: updated.price, updatedAt: this.now() }))
          : [];
      this.put('segments', [updated, ...siblings]);
      this.put('changes', [{ id: newId('chg_'), segmentId: existingId, at: this.now(), summary: 'Edited by hand' }]);
      return { ok: true, problems: [] };
    }
    const result = mergeIntoTrips({
      trips: this.state.trips,
      segments: this.state.segments,
      incoming: [valid],
      emailId: '',
      method: 'manual',
      now: this.now(),
      homeCodes: this.state.settings.homeCodes,
    });
    this.put('trips', result.trips);
    this.put('segments', result.segments.map((s) => ({ ...s, source: { parsedBy: 'manual' as const } })));
    this.put('changes', result.changes.map((c) => ({ ...c, emailId: undefined })));
    return { ok: true, problems: [] };
  }

  deleteSegment(segmentId: string): void {
    const seg = this.state.segments.find((s) => s.id === segmentId);
    if (!seg) return;
    this.drop('segments', [segmentId]);
    this.toast(`Deleted ${seg.title}`, 'Undo', () => this.put('segments', [seg]));
  }

  renameTrip(tripId: string, title: string): void {
    const trip = this.state.trips.find((t) => t.id === tripId);
    if (trip && title.trim()) this.put('trips', [{ ...trip, title: title.trim() }]);
  }

  resolveEmail(emailId: string, seg: ExtractedSegment): { ok: boolean; problems: string[] } {
    if (this.ui.offline) return { ok: false, problems: [OFFLINE_EDIT] };
    const email = this.state.emails.find((e) => e.id === emailId);
    if (!email) return { ok: false, problems: ['Email not found'] };
    const v = validateSegments([seg]);
    if (v.failed.length) return { ok: false, problems: v.failed[0].problems };
    const result = mergeIntoTrips({
      trips: this.state.trips,
      segments: this.state.segments,
      incoming: v.ok,
      emailId,
      method: 'manual',
      now: this.now(),
      homeCodes: this.state.settings.homeCodes,
    });
    this.put('trips', result.trips);
    this.put('segments', result.segments);
    this.put('changes', result.changes);
    this.put('emails', [{ ...email, status: 'parsed', problems: undefined } satisfies InboundEmail]);
    return { ok: true, problems: [] };
  }

  ignoreEmail(emailId: string): void {
    const email = this.state.emails.find((e) => e.id === emailId);
    if (email) this.put('emails', [{ ...email, status: 'ignored' }]);
  }
}

// ------------------------------------------------------------ React hooks

let storeInstance: AppStore | undefined;

export function setStore(s: AppStore): void {
  storeInstance = s;
}

export function getStore(): AppStore {
  if (!storeInstance) throw new Error('Store not initialized');
  return storeInstance;
}

export function useAppState<T>(select: (s: AppState) => T): T {
  const store = getStore();
  return useSyncExternalStore(store.subscribe, () => select(store.state), () => select(store.state));
}

export function useUi(): UiState {
  const store = getStore();
  return useSyncExternalStore(store.subscribe, () => store.ui, () => store.ui);
}

/** Whether a switched feature shows on this phone right now (see _shared/features.ts). Safe before the first load. */
export function useFeature(key: FeatureKey): boolean {
  const store = getStore();
  return useSyncExternalStore(store.subscribe, () => store.isOn(key), () => store.isOn(key));
}

/** Bookings to show: flights imported from the history sheet only while Past flights is on. */
export function useSegments(): Segment[] {
  const segments = useAppState((s) => s.segments);
  const showImported = useFeature('pastFlights');
  return useMemo(() => (showImported ? segments : segments.filter((s) => s.source.parsedBy !== 'import')), [segments, showImported]);
}

/** Michael (who sees Settings → Developer). */
export function useIsOwner(): boolean {
  const store = getStore();
  return useSyncExternalStore(store.subscribe, () => store.isOwner, () => store.isOwner);
}
